import { Button } from "@dropbox/dig-components/buttons";
import { Modal } from "@dropbox/dig-components/modal";
import { Spinner } from "@dropbox/dig-components/progress_indicators";
import { Box } from "@dropbox/dig-foundations";
import { PAP_Cancel_DashInviteModal } from "@mirage/analytics/events/types/cancel_dash_invite_modal";
import { PAP_Dismiss_DashInviteModal } from "@mirage/analytics/events/types/dismiss_dash_invite_modal";
import { PAP_Dismiss_DashInviteModalResult } from "@mirage/analytics/events/types/dismiss_dash_invite_modal_result";
import { PAP_Shown_DashInviteModal } from "@mirage/analytics/events/types/shown_dash_invite_modal";
import { PAP_Shown_DashInviteModalResult } from "@mirage/analytics/events/types/shown_dash_invite_modal_result";
import { hideSnackbar, showSnackbar } from "@mirage/shared/snackbar";
import { usePrevious } from "@mirage/shared/util/hooks";
import i18n from "@mirage/translations";
import { useCallback, useEffect, useMemo } from "react";
import { enforceExhaustive } from "../util";
import { useDashTeamInvitePapLogger } from "./hooks/useDashTeamInvitePapLogger";
import { useManagedInviteFormWithInitialData } from "./hooks/useManagedInviteForm";
import { InviteForm } from "./InviteForm";
import styles from "./InviteModal.module.css";
import {
  InviteModalSubmitPartialSuccessContent,
  InviteModalSubmitSuccessContent,
} from "./InviteModalPostSubmit";
import { OverQuotaLicenseLimit } from "./OverQuotaLicenseLimit";
import { DashInviteFormSource, InviteFormStatus } from "./types";
import { maybeMockFetchWithTestData } from "./utils/mockFetch";

import { OverQuotaMemberLimit } from "./OverQuotaMemberLimit";
/**
 * Setting up api stub gated env + manual flag
 * This is considered a temporary measure for dev work until we evaluate more robust options
 */
maybeMockFetchWithTestData();

export function InviteModal({ onClose }: { onClose: () => void }) {
  const { logDashTeamInvitePapEvent } = useDashTeamInvitePapLogger();
  const {
    handleSubmit,
    inviteFormStatus,
    managedInviteFormProps,
    submissionResult,
  } = useManagedInviteFormWithInitialData({
    initialMessage: i18n.t("optional_message_default"),
    defaultInitialEmptyInputCount: 4,
    dashInviteFormSource: DashInviteFormSource.DASH_HOMEPAGE_MODAL,
  });

  const { individualContacts, validateBatchContacts } =
    managedInviteFormProps ?? {};

  useEffect(() => {
    logDashTeamInvitePapEvent(PAP_Shown_DashInviteModal);
  }, [logDashTeamInvitePapEvent]);

  const handleClickCancel = useCallback(() => {
    logDashTeamInvitePapEvent(PAP_Cancel_DashInviteModal);
    onClose();
  }, [onClose, logDashTeamInvitePapEvent]);

  const handleRequestClose = useCallback(() => {
    logDashTeamInvitePapEvent(PAP_Dismiss_DashInviteModal);
    hideSnackbar();
    onClose();
  }, [onClose, logDashTeamInvitePapEvent]);

  const handleRequestCloseResult = useCallback(() => {
    logDashTeamInvitePapEvent(PAP_Dismiss_DashInviteModalResult);
    onClose();
  }, [onClose, logDashTeamInvitePapEvent]);

  const handleOpenResult = useCallback(() => {
    if (submissionResult) {
      logDashTeamInvitePapEvent(PAP_Shown_DashInviteModalResult, {
        inviteSuccessCount: submissionResult.successfullyInvitedEmails.length,
        inviteFailureCount: submissionResult.failedEmails.length,
      });
    }
  }, [logDashTeamInvitePapEvent, submissionResult]);

  // save the previous inviteFormStatus
  // so we can compare and display snackbar when inviteFormStatus changes
  const inviteFormStatusPrevious = usePrevious(inviteFormStatus);

  const renderModalContent = () => {
    switch (inviteFormStatus) {
      case InviteFormStatus.SUBMITTED:
        if (submissionResult) {
          if (
            submissionResult.failedEmails.length > 0 ||
            !!submissionResult.error
          ) {
            return (
              <>
                <InviteModalSubmitPartialSuccessContent.Header
                  {...submissionResult}
                  onClose={handleRequestCloseResult}
                />
                <InviteModalSubmitPartialSuccessContent.Body
                  {...submissionResult}
                  onClose={handleRequestCloseResult}
                  onOpen={handleOpenResult}
                />
                <InviteModalSubmitPartialSuccessContent.Footer
                  onClose={handleRequestCloseResult}
                />
              </>
            );
          }
          return (
            <>
              <InviteModalSubmitSuccessContent.Header
                {...submissionResult}
                onClose={handleRequestCloseResult}
              />
              <InviteModalSubmitSuccessContent.Body
                {...submissionResult}
                onClose={handleRequestCloseResult}
                onOpen={handleOpenResult}
              />
              <InviteModalSubmitSuccessContent.Footer
                onClose={handleRequestCloseResult}
              />
            </>
          );
        }
        return null;
      case InviteFormStatus.LOADING:
        return (
          <>
            <Modal.Header hasBottomSpacing="title-small">
              <Modal.Title size="medium" weightVariant="emphasized">
                {i18n.t("invite_modal_title")}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <InviteModalLoadingSpinner />
            </Modal.Body>
          </>
        );
      case InviteFormStatus.CAN_SUBMIT:
      case InviteFormStatus.SUBMITTING:
      case InviteFormStatus.CANNOT_SUBMIT:
        return (
          <>
            <Modal.Header hasBottomSpacing="title-small">
              <Modal.Title size="medium" weightVariant="emphasized">
                {i18n.t("invite_modal_title")}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {managedInviteFormProps ? (
                <InviteForm {...managedInviteFormProps} />
              ) : (
                <InviteModalLoadingSpinner />
              )}
            </Modal.Body>
            <Modal.Footer
              preferComposition={true}
              className={styles.inviteModalFooter}
            >
              <Button
                variant="opacity"
                disabled={inviteFormStatus === InviteFormStatus.SUBMITTING}
                onClick={handleClickCancel}
              >
                {i18n.t("disconnect_cancel")}
              </Button>
              <Button
                variant="primary"
                disabled={!(inviteFormStatus === InviteFormStatus.CAN_SUBMIT)}
                isLoading={inviteFormStatus === InviteFormStatus.SUBMITTING}
                onClick={
                  inviteFormStatus === InviteFormStatus.CAN_SUBMIT
                    ? handleSubmit
                    : undefined
                }
              >
                {i18n.t("invite_button_text")}
              </Button>
            </Modal.Footer>
          </>
        );

      case InviteFormStatus.OVER_QUOTA_LICENSE_LIMIT:
        return (
          <>
            <Modal.Header hasBottomSpacing="title-small">
              <Modal.Title size="medium" weightVariant="emphasized">
                {i18n.t("invite_modal_title")}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <OverQuotaLicenseLimit
                licenseCount={
                  managedInviteFormProps?.initialInviteFormData.licenseCount ??
                  0
                }
                isAdmin={
                  !!managedInviteFormProps?.initialInviteFormData
                    .canInviteAllTeamMembers
                }
              />
            </Modal.Body>
          </>
        );

      case InviteFormStatus.OVER_QUOTA_MEMBER_LIMIT:
        return (
          <>
            <Modal.Header hasBottomSpacing="title-small">
              <Modal.Title size="medium" weightVariant="emphasized">
                {i18n.t("invite_modal_title")}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <OverQuotaMemberLimit
                isAdmin={
                  !!managedInviteFormProps?.initialInviteFormData
                    .canInviteAllTeamMembers
                }
              />
            </Modal.Body>
          </>
        );
      default:
        return enforceExhaustive(inviteFormStatus);
    }
  };

  const showCloseButton = !(
    inviteFormStatus === InviteFormStatus.SUBMITTED &&
    submissionResult?.failedEmails.length === 0 &&
    !submissionResult?.error
  );

  const snackbarSubmittingStatus = useMemo(
    () => ({
      title: i18n.t("invite_modal_sending_invites", {
        count:
          (managedInviteFormProps?.validIndividualContactCount ?? 0) +
          (managedInviteFormProps?.validTeamContactCount ?? 0),
      }),
      showSpinner: true,
      timeoutMs: 0,
      hideCloseButton: true,
      fullWidth: true,
      containerClassName: styles.snackbarContainer,
    }),
    [
      managedInviteFormProps?.validIndividualContactCount,
      managedInviteFormProps?.validTeamContactCount,
    ],
  );

  const snackbarSubmissionErrorStatus = useMemo(
    () => ({
      title: i18n.t("invites_failed_error_message"),
      showSpinner: false,
      timeoutMs: 5000,
      hideCloseButton: false,
      fullWidth: true,
      containerClassName: styles.snackbarContainer,
    }),
    [],
  );

  useEffect(() => {
    if (
      inviteFormStatusPrevious === InviteFormStatus.SUBMITTING &&
      inviteFormStatus === InviteFormStatus.CAN_SUBMIT &&
      !!submissionResult?.error?.[".tag"] &&
      !!validateBatchContacts &&
      !!individualContacts
    ) {
      validateBatchContacts(individualContacts);
    }
  }, [
    individualContacts,
    inviteFormStatus,
    inviteFormStatusPrevious,
    submissionResult,
    validateBatchContacts,
  ]);

  // show snackbar in this priority order
  // 1. submitting
  // 2. submission error if previous status was submitting
  // 3. hide snackbar
  useEffect(() => {
    if (inviteFormStatus === InviteFormStatus.SUBMITTING) {
      showSnackbar(snackbarSubmittingStatus);
    } else if (
      inviteFormStatusPrevious === InviteFormStatus.SUBMITTING &&
      !!submissionResult?.error?.[".tag"]
    ) {
      showSnackbar(snackbarSubmissionErrorStatus);
    } else if (inviteFormStatus === InviteFormStatus.SUBMITTED) {
      hideSnackbar();
    }
  }, [
    inviteFormStatus,
    inviteFormStatusPrevious,
    snackbarSubmittingStatus,
    snackbarSubmissionErrorStatus,
    submissionResult,
  ]);

  return (
    <Modal
      open={true}
      withCloseButton={showCloseButton ? "close" : undefined}
      onRequestClose={handleRequestClose}
      isCentered
    >
      {renderModalContent()}
    </Modal>
  );
}

const InviteModalLoadingSpinner: React.FC = () => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      className={styles.inviteModalLoadingSpinnerContainer}
    >
      <Spinner
        size="large"
        aria-valuetext="Loading"
        data-testid="invite-modal-loading-spinner"
      />
    </Box>
  );
};
